// ───────────────────────────────────────────────
// API client. Uses relative URLs — Vite proxies /api → :8000 in dev,
// FastAPI serves both the static app and /api in prod.
//
// The fetch transport (session cookie, centralized 401 handling, FastAPI
// error coercion) lives in shared/api-core.js so the admin SPA shares the
// exact same wrapper. This file owns only the frontend endpoint map plus the
// active-archer header it injects via the shared provider hook.
// ───────────────────────────────────────────────

import {
  request,
  setUnauthorizedHandler,
  setExtraHeadersProvider,
} from "../../../shared/api-core.js";

// Re-export the shared 401 handler registration so AuthProvider keeps its
// existing import surface (`import { setUnauthorizedHandler } from "./api"`).
export { setUnauthorizedHandler };

// A failed request's machine-readable error code (#2052) is read via
// `apiErrorCode` from lib/apiError — deliberately NOT re-exported here, since
// page tests mock this module wholesale and would mock the parser away too.

// Derive the thumbnail variant of a `/media/...` photo URL (#1219). List/roster
// avatars render at ~40px, so they request a small `*.thumb.<ext>` variant the
// backend generates on upload (and lazily backfills for older photos). The full
// profile view keeps the original. A non-`/media/` URL is returned unchanged.
export function mediaThumbUrl(photoUrl) {
  if (!photoUrl || !photoUrl.startsWith("/media/")) return photoUrl;
  const dot = photoUrl.lastIndexOf(".");
  if (dot <= photoUrl.lastIndexOf("/")) return photoUrl; // no extension
  return `${photoUrl.slice(0, dot)}.thumb${photoUrl.slice(dot)}`;
}

// The active archer's id, kept in sync by AuthProvider. When set it rides
// along as the X-Active-Archer header so the API scopes data to it. The header
// is injected into every shared-transport request via the provider hook below.
let activeArcherId = null;

export function setActiveArcherId(id) {
  if (id !== activeArcherId) {
    // Archer-scoped reference data (tags) no longer applies once the active
    // archer changes — drop it so the next read fetches the new archer's set.
    invalidateReferenceCache("tags");
  }
  activeArcherId = id;
}

// The id of the archer currently in scope. Read by components that the active
// archer is not threaded into as a prop (e.g. the Goals slide in PBCarousel).
export function getActiveArcherId() {
  return activeArcherId;
}

setExtraHeadersProvider(() =>
  activeArcherId != null ? { "X-Active-Archer": String(activeArcherId) } : null,
);

// ───────────────────────────────────────────────
// Reference-data cache.
//
// Static catalog data (round types, tags) changes rarely — round types only
// on an admin edit. Without caching, every component mount that reads it fires
// a fresh request: the new-round wizard alone refetched the round-type catalog
// on every TYPE → FORMAT navigation. We keep a tiny module-level cache that
// stores the resolved value AND the in-flight promise, so concurrent and
// repeat reads within a session collapse to a single network round-trip.
//
// Entries are invalidated explicitly (admin catalog edit, tag mutation, or an
// active-archer switch) via `invalidateReferenceCache`. A rejected fetch is
// never cached, so a transient failure does not poison subsequent reads.
// ───────────────────────────────────────────────
const referenceCache = new Map();

function cachedRequest(key, path, opts) {
  const hit = referenceCache.get(key);
  if (hit) return hit.promise;

  const promise = request(path, opts).catch((err) => {
    // Drop the failed entry so the next caller retries instead of replaying
    // a rejected promise forever.
    if (referenceCache.get(key)?.promise === promise) referenceCache.delete(key);
    throw err;
  });
  referenceCache.set(key, { promise });
  return promise;
}

// Bust cached reference data. Pass a key to drop a single entry, or omit it to
// clear everything (e.g. on an active-archer switch where archer-scoped tags
// no longer apply). Exported so admin catalog saves and tag mutations can keep
// the client cache honest without a hard refresh.
export function invalidateReferenceCache(key) {
  if (key == null) referenceCache.clear();
  else referenceCache.delete(key);
}

// Seed the in-memory reference cache with an in-flight (or resolved) promise so
// the next `cachedRequest(key, …)` read serves it WITHOUT a fresh network call.
// Used after a mutation does a guaranteed-fresh (cache-busting) refetch: priming
// the cache means the immediately-following consumer (e.g. the wizard remount)
// reads the new data, not a stale browser-HTTP-cached default fetch. A rejected
// fetch is dropped so it never poisons subsequent reads (mirrors cachedRequest).
function primeReferenceCache(key, promise) {
  const guarded = promise.catch((err) => {
    if (referenceCache.get(key)?.promise === guarded) referenceCache.delete(key);
    throw err;
  });
  referenceCache.set(key, { promise: guarded });
  return guarded;
}

// Serialize the shared filter-params object (buildApiParams output) to the
// RoundFilterParams query string the backend accepts. THE one writer for every
// caller that forwards the FULL filter shape — including the multi-value
// `faces` dimension: the /stats* panels (getArcherStats, getEquipmentStats,
// getHandicap #1996) AND the round-list routes rounds()/roundsPage(), which
// used to carry their own hand-listed FILTER_KEYS arrays and consequently
// dropped `arrows_per_end` (#2029). Routes already scoped to a single face
// keep their own scalar-only loops.
function statsFilterQs(params = {}) {
  const qs = new URLSearchParams();
  if (params.faces?.length)      { params.faces.forEach((f) => qs.append("faces", f)); if (params.faces_op) qs.set("faces_op", params.faces_op); }
  if (params.kind)               { qs.set("kind", params.kind);                        if (params.kind_op)      qs.set("kind_op",      params.kind_op); }
  if (params.location)           { qs.set("location", params.location);                if (params.location_op)  qs.set("location_op",  params.location_op); }
  if (params.bow_style)          { qs.set("bow_style", params.bow_style);              if (params.bow_style_op) qs.set("bow_style_op", params.bow_style_op); }
  if (params.loadout_id != null) { qs.set("loadout_id", String(params.loadout_id));    if (params.loadout_op)   qs.set("loadout_op",   params.loadout_op); }
  if (params.tag_ids)            { qs.set("tag_ids", params.tag_ids); qs.set("tag_op", params.tag_op ?? "any"); }
  if (params.date_from)          { qs.set("date_from", params.date_from);              if (params.date_from_op) qs.set("date_from_op", params.date_from_op); }
  if (params.date_to)            { qs.set("date_to", params.date_to);                  if (params.date_to_op)   qs.set("date_to_op",   params.date_to_op); }
  if (params.distance_m != null) { qs.set("distance_m", String(params.distance_m));   if (params.distance_op)  qs.set("distance_op",  params.distance_op); }
  if (params.ends_min != null)   { qs.set("ends_min", String(params.ends_min));        if (params.ends_min_op)  qs.set("ends_min_op",  params.ends_min_op); }
  if (params.ends_max != null)   { qs.set("ends_max", String(params.ends_max));        if (params.ends_max_op)  qs.set("ends_max_op",  params.ends_max_op); }
  if (params.arrows_per_end != null) qs.set("arrows_per_end", String(params.arrows_per_end));
  if (params.score_min != null)  { qs.set("score_min", String(params.score_min));      if (params.score_min_op) qs.set("score_min_op", params.score_min_op); }
  if (params.score_max != null)  { qs.set("score_max", String(params.score_max));      if (params.score_max_op) qs.set("score_max_op", params.score_max_op); }
  return qs;
}

export const api = {
  health: () => request("/api/health"),
  me: () => request("/api/archers/me"),
  archer: (id) => request(`/api/archers/${id}`),
  updateArcher: (id, payload) =>
    request(`/api/archers/${id}`, { method: "PATCH", body: payload }),
  // Persist the archer's Stats-dashboard panel order (#1538). `order` is an
  // ordered list of stable panel IDs; null/[] resets to the default order.
  updateStatsPanelOrder: (id, order) =>
    request(`/api/archers/${id}`, {
      method: "PATCH",
      body: { stats_panel_order: order },
    }),
  // Persist the archer's last-selected Shot Calendar window (30/90/365) (#1614).
  updateCalendarWindow: (id, days) =>
    request(`/api/archers/${id}`, {
      method: "PATCH",
      body: { calendar_window_days: days },
    }),
  // Persist the archer's stats-carousel tab selections (#1628). `prefs` is the
  // full merged object, e.g. { pb: 0, fade: "day", calendar: 2 }.
  updateStatsTabPrefs: (id, prefs) =>
    request(`/api/archers/${id}`, {
      method: "PATCH",
      body: { stats_tab_prefs: prefs },
    }),
  uploadArcherPhoto: (id, file) => {
    const form = new FormData();
    form.append("file", file);
    return request(`/api/archers/${id}/photo`, { method: "POST", body: form });
  },

  // Auth & identity
  authMe: () => request("/api/auth/me"),
  authConfig: () => request("/api/auth/config"),
  signup: (payload) => request("/api/auth/signup", { method: "POST", body: payload }),
  login: (payload) => request("/api/auth/login", { method: "POST", body: payload }),
  logout: () => request("/api/auth/logout", { method: "POST" }),
  updateAccount: (payload) =>
    request("/api/auth/account", { method: "PATCH", body: payload }),
  createArcher: (payload) =>
    request("/api/archers", { method: "POST", body: payload }),
  verifyEmail: (token) =>
    request("/api/auth/verify-email", { method: "POST", body: { token } }),
  forgotPassword: (email) =>
    request("/api/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (payload) =>
    request("/api/auth/reset-password", { method: "POST", body: payload }),

  // Archer invitations — claim (promote to own account) & co-manage
  inviteClaim: (archerId, email) =>
    request(`/api/archers/${archerId}/claim`, { method: "POST", body: { email } }),
  claimArcher: (archerId, email) =>
    request(`/api/archers/${archerId}/claim`, { method: "POST", body: { email } }),
  invitationInfo: (token) => request(`/api/invitations/${token}`),
  acceptInvite: (token, { password, name } = {}) =>
    request(`/api/invitations/${token}/accept`, {
      method: "POST",
      body: { password, name },
    }),
  inviteCoManager: (archerId, { email, role }) =>
    request(`/api/archers/${archerId}/managers`, {
      method: "POST",
      body: { email, role },
    }),
  archerManagers: (archerId) => request(`/api/archers/${archerId}/managers`),
  removeManager: (archerId, userId) =>
    request(`/api/archers/${archerId}/managers/${userId}`, { method: "DELETE" }),

  rounds: (params = {}) => {
    // Filter block via the shared statsFilterQs writer — the frontend mirror of
    // the backend's single RoundFilterParams dependency (#2029), so History and
    // Stats can never end up supporting different filter dimensions.
    const qs = statsFilterQs(params);
    if (params.archer_id != null) qs.set("archer_id", params.archer_id);
    if (params.active != null)    qs.set("active", String(params.active));
    if (params.limit != null)     qs.set("limit", String(params.limit));
    // participation is a History-only viewer-role dimension, not a round filter.
    if (params.participation != null) qs.set("participation", String(params.participation));
    // Opt-in payload extras (#2055). `include: "thumbnails"` restores
    // shot_positions + end_summaries, which the list no longer ships by
    // default — only the card surfaces that draw the shot heatmap need them.
    if (params.include != null)   qs.set("include", String(params.include));
    const q = qs.toString();
    return request(`/api/rounds${q ? `?${q}` : ""}`);
  },
  // One server-driven page of round summaries: the generic Page[T] envelope
  // ({ items, total }, #1682) — `offset` and `limit` page server-side so
  // History never downloads more than one page. Filter params are forwarded
  // identically to rounds().
  roundsPage: (params = {}) => {
    const qs = statsFilterQs(params);
    if (params.archer_id != null) qs.set("archer_id", params.archer_id);
    if (params.active != null)    qs.set("active", String(params.active));
    if (params.offset != null)    qs.set("offset", String(params.offset));
    if (params.limit != null)     qs.set("limit", String(params.limit));
    if (params.participation != null) qs.set("participation", String(params.participation));
    if (params.include != null)   qs.set("include", String(params.include));
    const q = qs.toString();
    return request(`/api/rounds/page${q ? `?${q}` : ""}`);
  },
  // How many rounds the viewer has in one LOCAL calendar month (#2211).
  // A scalar — `{ month, timezone, total }` — so no page size bounds it.
  // Practice's "This month" counter used to filter rounds() client-side, which
  // capped the count at whatever `limit` the page happened to fetch.
  //
  // `month` is "YYYY-MM" in the archer's local calendar, and `tz` is the zone
  // it is measured in. BOTH are required for the answer to be right at the
  // month boundary, and the caller must resolve `tz` through
  // `dates.resolveTimeZone` — when the archer has stored no timezone the
  // browser's is the one the page renders dates with, and the server has no
  // way to know it.
  roundsMonthCount: ({ month, tz } = {}) => {
    const qs = new URLSearchParams({ month: String(month) });
    if (tz) qs.set("tz", tz);
    return request(`/api/rounds/count?${qs.toString()}`);
  },
  // Distinct target faces across the viewer's completed rounds — a lightweight
  // source for the History face picker (vs. fetching the full round set).
  roundFaces: () => request("/api/rounds/faces"),
  round: (id) => request(`/api/rounds/${id}`),
  // The NORMAL catalog read MUST revalidate against the server, not be served
  // a stale browser-HTTP-cache entry. Browsers that cached an OLD
  // `public, max-age=86400` response (before the server header was corrected to
  // `private, no-cache`) consider it fresh for 24h and satisfy a default fetch
  // from cache WITHOUT contacting the server — so a remembered personal round
  // (#1320) never appears on a fresh wizard load. `cache: "no-cache"` forces a
  // conditional request: the browser always revalidates, even a "fresh" entry,
  // and the server returns the current list. The in-memory referenceCache still
  // dedups repeat reads within a session, so this is at most one conditional
  // request per session. Scoped to round-types only — tags etc. keep defaults.
  roundTypes: () =>
    cachedRequest("round-types", "/api/round-types", { cache: "no-cache" }),
  // {face_code: "WA"|"NFAA"|"3D"|"Other"} — server-driven Stats-dashboard face
  // bucketing (#1760), sourced from the same shared face-definitions catalog
  // as the round-type list above. Same private/no-cache reasoning as
  // roundTypes: a future face addition must never be masked by a stale
  // browser-HTTP-cached response.
  faceGroups: () =>
    cachedRequest("face-groups", "/api/face-groups", { cache: "no-cache" }),
  // The active archer's last-used start choices for one Format (#2317) — face,
  // segments, loadout, inner_ten, bow_style, kind, tags, and (for a
  // bale-hosted Format) the remembered roster. Auto-remembered server-side on
  // every start; nothing here writes it. A 404 means "never started this
  // Format" — a normal, common state — so it resolves to `null` rather than
  // throwing, matching `getArcherScatter`'s "no data yet" pattern. Per-archer
  // and mutated by every start, so `private, no-cache`: never served from a
  // stale browser cache the way a long-lived catalog entry could be.
  getFormatStartDefaults: async (roundTypeId) => {
    try {
      return await request(`/api/round-types/${roundTypeId}/start-defaults`, {
        cache: "no-cache",
      });
    } catch (err) {
      if (err?.status === 404) return null;
      throw err;
    }
  },
  // One-call start from a Format (#2318) — the Home tile Start button. The
  // server resolves, in order, `overrides` -> the active archer's remembered
  // start-defaults (#2317) -> the Format's own catalog defaults, then creates
  // either a solo round or (when the resolved roster holds 2+ archers) a bale
  // session, bumping the remembered row's use_count itself. Response is a
  // discriminated `{kind: "round"|"bale", round, bale}` — exactly one of
  // `round` / `bale` is populated depending on `kind`. `overrides` is a
  // subset of the RoundCreate/BaleSessionCreate fields (kind, target_face,
  // equipment_loadout_id, inner_ten, tag_ids, event_name, ...) — it can never
  // carry ends_total/arrows_per_end/segments, which always come from the
  // Format server-side (#851).
  startFormat: (roundTypeId, overrides = {}) =>
    request(`/api/round-types/${roundTypeId}/start`, {
      method: "POST",
      body: overrides,
    }),
  // Save a custom round as a reusable PERSONAL round type (#1320). The server
  // sets owner_archer_id to the active archer and derives every catalog field
  // from the submitted stage list — the response is a full RoundTypeOut.
  createPersonalRoundType: async (payload) => {
    const rt = await request("/api/round-types/personal", { method: "POST", body: payload });
    // The new personal round must surface in the catalog list immediately. The
    // server's response for /api/round-types is HTTP-cacheable, so merely busting
    // the in-memory cache is not enough — a default refetch would be served STALE
    // (without the new round) by the browser HTTP cache. So bust the in-memory
    // entry AND seed it with a `cache: 'no-store'` refetch that guarantees a
    // browser-cache miss, so the next wizard mount reads fresh data including the
    // new round. The refetch failing is non-fatal: it self-evicts (primeReference
    // Cache) and the next read retries, so the create still resolves with `rt`.
    invalidateReferenceCache("round-types");
    primeReferenceCache(
      "round-types",
      request("/api/round-types", { cache: "no-store" }),
    ).catch(() => {});
    return rt;
  },
  // Formats library (#2316): edit / duplicate / archive a personal round type.
  // Each mutates what `roundTypes()` returns, so all three bust the catalog
  // cache and re-prime it with a `cache: 'no-store'` refetch — the exact same
  // reasoning as `createPersonalRoundType` above: a bare `invalidateReference
  // Cache` alone risks the next read being served a STALE browser-HTTP-cache
  // entry (`/api/round-types` is `private, no-cache`, not `no-store`).
  //
  // Edit a personal round type in place. Only the fields sent are changed;
  // `segments`, when sent, replaces the stage list and the server re-derives
  // `distances`/`ends`/`arrows_per_end`/`max_score`. Returns the updated
  // RoundTypeOut.
  updatePersonalRoundType: async (id, payload) => {
    const rt = await request(`/api/round-types/personal/${id}`, {
      method: "PATCH",
      body: payload,
    });
    invalidateReferenceCache("round-types");
    primeReferenceCache(
      "round-types",
      request("/api/round-types", { cache: "no-store" }),
    ).catch(() => {});
    return rt;
  },
  // Copy any visible round type (catalog or the caller's own personal) into a
  // new PERSONAL row owned by the caller — "duplicate to customise" without
  // touching the source. Returns the new RoundTypeOut.
  duplicateRoundType: async (id) => {
    const rt = await request(`/api/round-types/${id}/duplicate`, { method: "POST" });
    invalidateReferenceCache("round-types");
    primeReferenceCache(
      "round-types",
      request("/api/round-types", { cache: "no-store" }),
    ).catch(() => {});
    return rt;
  },
  // Archive (never hard-delete — a Round may still reference it) a personal
  // round type. 204 on success. The default `roundTypes()` list excludes an
  // archived row (the server's `GET /api/round-types?include=archived` would
  // surface it again — no frontend surface reads that flag yet, #2316 Wave 1
  // is backend + this API layer only).
  archivePersonalRoundType: async (id) => {
    await request(`/api/round-types/personal/${id}`, { method: "DELETE" });
    invalidateReferenceCache("round-types");
    primeReferenceCache(
      "round-types",
      request("/api/round-types", { cache: "no-store" }),
    ).catch(() => {});
  },
  createRound: (payload) => request("/api/rounds", { method: "POST", body: payload }),
  confirmEnd: (roundId, arrows) =>
    request(`/api/rounds/${roundId}/ends`, {
      method: "POST",
      body: { arrows },
    }),
  skipEnd: (roundId, reason) =>
    request(`/api/rounds/${roundId}/ends/skip`, {
      method: "POST",
      body: { reason },
    }),
  undoLastEnd: (roundId) =>
    request(`/api/rounds/${roundId}/ends/last`, { method: "DELETE" }),
  // Replace the arrows of an already-confirmed end in place — later ends are
  // untouched and the round's running totals recalculate on the next read.
  updateEnd: (roundId, endNumber, arrows) =>
    request(`/api/rounds/${roundId}/ends/${endNumber}`, {
      method: "PUT",
      body: { arrows },
    }),
  updateRound: (roundId, payload) =>
    request(`/api/rounds/${roundId}`, { method: "PATCH", body: payload }),
  // Correct a saved round's target_face (and/or per-segment face_size_cm)
  // after creation (#1766). Re-scores tap-to-drop/photo arrows server-side —
  // pass confirm_rescore: true once the caller has shown the archer the
  // preview count from the 422 the server returns when confirmation is
  // still needed.
  correctRoundFace: (roundId, payload) =>
    request(`/api/rounds/${roundId}/correct-face`, {
      method: "PATCH",
      body: payload,
    }),
  // Correct a saved round's distance_m (and every End.distance_m) after
  // creation (#1973) — e.g. it defaulted to 70m but was actually shot at
  // 50m. Never re-scores arrows; metadata-only. Two mutually exclusive
  // payload shapes: { distance_m } for a single-distance round (still 422s
  // with code "multi_distance_round" when the round spans several), or
  // { segments: [{ index, distance_m }] } (#1976) to correct individual
  // segments of a multi-segment/multi-distance round — each entry updates
  // only its own segment's ends.
  correctRoundDistance: (roundId, payload) =>
    request(`/api/rounds/${roundId}/correct-distance`, {
      method: "PATCH",
      body: payload,
    }),
  deleteRound: (roundId) =>
    request(`/api/rounds/${roundId}`, { method: "DELETE" }),
  abandonRound: (roundId) =>
    request(`/api/rounds/${roundId}/abandon`, { method: "POST" }),
  // Open-ended Simple Round (spec 2026-09-13): complete at the ends scored.
  finishRound: (roundId) =>
    request(`/api/rounds/${roundId}/finish`, { method: "POST" }),
  // remaining: "remove" (round shortens to the ends actually shot) or
  // "misses" (unshot ends recorded as zeros). ALWAYS send the explicit body —
  // never lean on the backend's bodiless default (#1819).
  endRoundEarly: (roundId, remaining) =>
    request(`/api/rounds/${roundId}/end-early`, {
      method: "POST",
      body: { remaining },
    }),
  // Ends every in-progress member round on a bale session with the same
  // "remove" | "zero" semantics as endRoundEarly, then finalizes the bale
  // session (#2436-ish sandbox QA sweep 2026-09-13).
  endBaleSessionEarly: (baleSessionId, remaining) =>
    request(`/api/bale-sessions/${baleSessionId}/end-early`, {
      method: "POST",
      body: { remaining },
    }),
  // Repair a round that was ended early with miss-filled ends (#1821):
  // deletes the trailing run of synthetic zero ends (skip_reason="early_end")
  // so stats count only what was actually shot. 409s when nothing repairable.
  removeSkippedEnds: (roundId) =>
    request(`/api/rounds/${roundId}/remove-skipped-ends`, { method: "POST" }),
  saveDraft: (roundId, arrows, expectedEndsDone = undefined) =>
    request(`/api/rounds/${roundId}/draft`, {
      method: "POST",
      body: expectedEndsDone != null
        ? { arrows, expected_ends_done: expectedEndsDone }
        : { arrows },
    }),
  clearDraft: (roundId) =>
    request(`/api/rounds/${roundId}/draft`, { method: "DELETE" }),

  getArcherStats: (archerId, params = {}) => {
    const q = statsFilterQs(params).toString();
    return request(`/api/archers/${archerId}/stats${q ? `?${q}` : ""}`);
  },

  // ── Goals (#1504) — archer score targets ──
  getGoals: (archerId) => request(`/api/archers/${archerId}/goals`),
  createGoal: (archerId, payload) =>
    request(`/api/archers/${archerId}/goals`, { method: "POST", body: payload }),
  updateGoal: (archerId, goalId, payload) =>
    request(`/api/archers/${archerId}/goals/${goalId}`, { method: "PATCH", body: payload }),
  deleteGoal: (archerId, goalId) =>
    request(`/api/archers/${archerId}/goals/${goalId}`, { method: "DELETE" }),

  // ── Arrow logs (M9.5 #1808) — quick-record of unscored practice volume ──
  // The full client surface (list/create/update/delete) lands together even
  // though the quick-record sheet (#1810) only creates: the post-round nudge
  // (#1811) and History integration (#1812) consume the rest, and adding them
  // here keeps api.js single-writer across those PRs.
  // List returns the Page[ArrowLogOut] envelope: { total, items, ... }.
  listArrowLogs: (archerId, { offset = 0, limit = 50 } = {}) => {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    return request(`/api/archers/${archerId}/arrow-logs?${qs}`);
  },
  createArrowLog: (archerId, payload) =>
    request(`/api/archers/${archerId}/arrow-logs`, { method: "POST", body: payload }),
  updateArrowLog: (id, payload) =>
    request(`/api/arrow-logs/${id}`, { method: "PATCH", body: payload }),
  deleteArrowLog: (id) =>
    request(`/api/arrow-logs/${id}`, { method: "DELETE" }),
  getArcherScatter: async (archerId, face, { limit = 15, excludeRoundId, ...filterParams } = {}) => {
    const qs = new URLSearchParams({ face: String(face), limit: String(limit) });
    if (excludeRoundId != null) qs.set("exclude_round_id", String(excludeRoundId));
    for (const [k, v] of Object.entries(filterParams)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    try {
      return await request(`/api/archers/${archerId}/stats/scatter?${qs}`);
    } catch (err) {
      // A 404 here is an expected, normal condition: the archer (e.g. a
      // co-archer on a bale-session round) simply has no scatter history for
      // this face (issue #1304). Treat it as "no data" rather than surfacing a
      // console error and breaking the overlay render. Other errors propagate.
      if (err?.status === 404) return null;
      throw err;
    }
  },

  // ═══ M9 Wave 2 stat methods — each agent REPLACES only its "(#NNNN: method here)" line ═══
  // ── M9-W2 [#1499] arrow-distribution method ──
  getArrowDistribution: (archerId, filterParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filterParams)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return request(`/api/archers/${archerId}/stats/arrow-distribution${q ? `?${q}` : ""}`);
  },
  // ── M9-W2 [#1501] end-profile method (reworked #1582, two tabs #1584) ──
  // Conditioning (fade) profile: avg per-arrow score vs cumulative arrows shot.
  // `mode` selects the framing: "round_length" (one curve per round length) or
  // "day" (one curve, arrows chained across the calendar day). Only the stats
  // filter params are forwarded alongside `mode`.
  getEndProfile: (archerId, filterParams = {}, mode = "round_length") => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filterParams)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    qs.set("mode", mode);
    const q = qs.toString();
    return request(`/api/archers/${archerId}/stats/end-profile${q ? `?${q}` : ""}`);
  },
  // ── M9-W2 [#1502] arrow-profile method ──
  getArrowProfile: async (archerId, filterParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filterParams)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return request(`/api/archers/${archerId}/stats/arrow-profile${q ? `?${q}` : ""}`);
  },
  // ── M9-W2 [#1505] equipment method ──
  getEquipmentStats: (archerId, params = {}) => {
    const q = statsFilterQs(params).toString();
    return request(`/api/archers/${archerId}/stats/equipment${q ? `?${q}` : ""}`);
  },
  // ── M9-W2 [#1507] end-distribution method ──
  getEndDistribution: (archerId, filterParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filterParams)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return request(`/api/archers/${archerId}/stats/end-distribution${q ? `?${q}` : ""}`);
  },
  // ── M9-W2 [#1508] group-analysis method ──
  getGroupAnalysis: (archerId, face, filterParams = {}) => {
    const qs = new URLSearchParams({ face: String(face) });
    for (const [k, v] of Object.entries(filterParams)) {
      if (v != null && v !== "") qs.set(k, String(v));
    }
    return request(`/api/archers/${archerId}/stats/group-analysis?${qs}`);
  },

  // Equipment loadouts
  getLoadouts: () => request("/api/equipment/loadouts"),
  createLoadout: (payload) => request("/api/equipment/loadouts", { method: "POST", body: payload }),
  updateLoadout: (id, payload) => request(`/api/equipment/loadouts/${id}`, { method: "PATCH", body: payload }),
  deleteLoadout: (id) => request(`/api/equipment/loadouts/${id}`, { method: "DELETE" }),

  getGroupSize: (roundId) => request(`/api/rounds/${roundId}/group-size`),

  // #1996: carries the same stats filter params as getArcherStats so the
  // handicap window is computed over the filtered round set.
  getHandicap: (archerId, params = {}) => {
    const q = statsFilterQs(params).toString();
    return request(`/api/archers/${archerId}/handicap${q ? `?${q}` : ""}`);
  },

  getSavedFilters: () => request("/api/filters"),
  saveFilter: (payload) => request("/api/filters", { method: "POST", body: payload }),
  deleteSavedFilter: (id) => request(`/api/filters/${id}`, { method: "DELETE" }),
  getTrends: (archerId) => request(`/api/archers/${archerId}/trends`),
  getTags: () => cachedRequest("tags", "/api/tags"),
  createTag: (payload) =>
    request("/api/tags", { method: "POST", body: payload }).then((r) => {
      invalidateReferenceCache("tags");
      return r;
    }),
  updateTag: (id, payload) =>
    request(`/api/tags/${id}`, { method: "PATCH", body: payload }).then((r) => {
      invalidateReferenceCache("tags");
      return r;
    }),
  deleteTag: (id) =>
    request(`/api/tags/${id}`, { method: "DELETE" }).then((r) => {
      invalidateReferenceCache("tags");
      return r;
    }),
  addRoundTag: (roundId, tagId) => request(`/api/rounds/${roundId}/tags/${tagId}`, { method: "POST" }),
  removeRoundTag: (roundId, tagId) => request(`/api/rounds/${roundId}/tags/${tagId}`, { method: "DELETE" }),

  // Bale sessions — group scoring where multiple archers share a bale
  createBaleSession: (payload) =>
    request("/api/bale-sessions", { method: "POST", body: payload }),
  getBaleSession: (id) => request(`/api/bale-sessions/${id}`),
  // SSE endpoint for a bale session. Returns a URL rather than a promise: the
  // caller hands it to `new EventSource(...)`, which owns the connection. This
  // is the one path that cannot go through `request()`, so the URL still has to
  // be built here — hooks/useBaleSession.js was assembling it inline, the sole
  // lib/api.js discipline violation in the app (#2068).
  baleSessionStreamUrl: (sessionId) => `/api/bale-sessions/${sessionId}/stream`,
  closeBaleSession: (sessionId) =>
    request(`/api/bale-sessions/${sessionId}/close`, { method: 'POST' }),
  getActiveBaleSession: () => request("/api/bale-sessions/active"),
  previewBaleSession: (token) => request(`/api/bale-sessions/preview/${token}`),
  joinBaleSession: (token, body) =>
    request(`/api/bale-sessions/join/${token}`, { method: "POST", body }),
  claimBaleScorecard: (token, archerId) =>
    request(`/api/bale-sessions/claim/${token}`, { method: "POST", body: { archer_id: archerId } }),
  updateBaleLanes: (sessionId, updates) =>
    request(`/api/bale-sessions/${sessionId}/lanes`, { method: "PATCH", body: updates }),
  patchBaleSessionScorer: (sessionId, userId) =>
    request(`/api/bale-sessions/${sessionId}/scorer`, { method: 'PATCH', body: { user_id: userId } }),
  patchScorerView: (sessionId, roundId) =>
    request(`/api/bale-sessions/${sessionId}/scorer-view`, {
      method: 'PATCH',
      body: { round_id: roundId },
    }),
  confirmBaleSessionEnd: (sessionId, expectedVersion = undefined) =>
    request(`/api/bale-sessions/${sessionId}/confirm-end`, {
      method: 'POST',
      body: expectedVersion !== undefined ? { expected_version: expectedVersion } : {},
    }),

  // Wizard invites — QR-based archer join flow for new round setup
  createWizardInvite: (targetFace = null, ttlMinutes = 60) =>
    request('/api/wizard-invites', { method: 'POST', body: { target_face: targetFace, ttl_minutes: ttlMinutes } }),
  regenerateWizardInvite: (token, ttlMinutes = 60) =>
    request(`/api/wizard-invites/${token}/regenerate`, { method: 'POST', body: { ttl_minutes: ttlMinutes } }),
  getWizardInvite: (token) => request(`/api/wizard-invites/${token}`),
  respondToWizardInvite: (token) => request(`/api/wizard-invites/${token}/respond`, { method: 'POST' }),
  getWizardInviteResponses: (token) => request(`/api/wizard-invites/${token}/responses`),
  updateWizardInvitePreferences: (token, prefs) =>
    request(`/api/wizard-invites/${token}/respond`, { method: 'PATCH', body: prefs }),

  // Paper scorecard OCR import
  submitOcrJob: (payload) =>
    request("/api/ocr/scorecard", { method: "POST", body: payload }),
  getOcrJob: (jobId) => request(`/api/ocr/scorecard/${jobId}`),
  // List the current user's import jobs (#1168). active=true (the default
  // server-side) returns only jobs needing attention: in flight, done but
  // unlinked, or failed — excluding dismissed. → {total, items}
  listOcrJobs: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.active != null) qs.set("active", String(params.active));
    if (params.skip != null)   qs.set("skip", String(params.skip));
    if (params.limit != null)  qs.set("limit", String(params.limit));
    const q = qs.toString();
    return request(`/api/ocr/scorecard${q ? `?${q}` : ""}`);
  },
  // Link an approved import to its round ({round_id}) or dismiss it
  // ({dismissed: true}) — exactly one key per call (#1168).
  updateOcrJob: (jobId, payload) =>
    request(`/api/ocr/scorecard/${jobId}`, { method: "PATCH", body: payload }),
  // Atomic approve (#1871): one call creates the round from the reviewed
  // draft, bulk-imports the ends, backdates started_at/completed_at to the
  // chosen session date (noon-UTC policy lives server-side), and links the
  // job — all in a single backend transaction. Returns the RoundSummary.
  approveScorecardJob: (jobId, payload) =>
    request(`/api/ocr/scorecard/${jobId}/approve`, { method: "POST", body: payload }),
  getOcrJobStages: (jobId) => request(`/api/ocr/jobs/${jobId}/stages`),
  // Dewarped scorecard image for the review screen (#1179). Plain URL for an
  // <img src> — the browser fetches it with cookies; legacy jobs 404 and the
  // image panel hides itself via onError.
  ocrJobImageUrl: (jobId) => `/api/ocr/scorecard/${jobId}/image`,
  // De-warped scorecard image for a paper-imported round (#1299). Plain URL for
  // an <img src> — the browser fetches it with cookies. Only request this when
  // round.has_scorecard_image is true; rounds without a stored card 404.
  getRoundScorecardImageUrl: (roundId) => `/api/rounds/${roundId}/scorecard-image`,
  // Printable HTML scorecard for a round (#1299). Plain URL for an <iframe src>
  // — the browser loads it with cookies. Rendered server-side as an A4 print
  // page; the in-page viewer focuses the iframe and calls print() to save as PDF.
  getRoundScorecardUrl: (roundId) => `/api/rounds/${roundId}/scorecard`,
  // Real PDF scorecard for a round. Plain URL for an <object data>/<iframe src>
  // or a <a href> download — the browser loads it with cookies. Embedded PDFs
  // cache aggressively by URL, so pass a `version` token derived from the
  // round's current state (e.g. confirmed-ends count + total) to bust the cache
  // when the scorecard changes; otherwise a round that gains more ends keeps
  // showing the stale cached PDF inside the iframe/object.
  getRoundScorecardPdfUrl: (roundId, version) =>
    `/api/rounds/${roundId}/scorecard.pdf${version != null ? `?v=${version}` : ""}`,
  bulkImportEnds: (roundId, ends) =>
    request(`/api/rounds/${roundId}/ends/bulk`, { method: "POST", body: { ends } }),
  // Attach a paper scorecard photo to an existing live-scored round. Image-only:
  // stores the photo and links it to the round for later retrieval — no OCR, no
  // scoring, no cross-check (deferred to #1312/M999). Read-only w.r.t. scores.
  // payload is { image_base64 }; returns { image_stored }.
  attachScorecard: (roundId, payload) =>
    request(`/api/rounds/${roundId}/attach-scorecard`, { method: "POST", body: payload }),

  // Recent round-type ids for the active archer (#1394). Derived from existing
  // round history — there is no dedicated "recents" endpoint and none is needed.
  // Returns up to `limit` distinct round_type_ids ordered most-recent-first, so
  // the FORMAT step can surface one-tap re-shoot rows above the full catalog.
  // Rounds with no round_type_id (fully custom) are skipped — they can't be
  // resolved back to a catalog row to re-select identically.
  recentRoundTypeIds: async (limit = 4) => {
    const rounds = await api.rounds({ active: false, limit: 40 });
    const seen = new Set();
    const ids = [];
    for (const r of Array.isArray(rounds) ? rounds : []) {
      const id = r.round_type_id;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
      if (ids.length >= limit) break;
    }
    return ids;
  },

  // ── Favourite round types (#1394 redesign) ─────────────────────────────────
  // Server-synced, NAMED favourites scoped to the active archer (the X-Active-
  // Archer header rides every request, so the server resolves the archer — the
  // caller no longer passes an archer id). Each favourite carries a CUSTOM
  // display label so the favourite card shows a name the archer chose. Replaces
  // the old client-only localStorage favourites; favourites now sync across the
  // archer's devices and can be (un)pinned and renamed.
  //   listFavouriteRoundTypes()            → [{ round_type_id, name }]
  //   addFavouriteRoundType(id, name)      → upsert (insert or rename)
  //   renameFavouriteRoundType(id, name)   → rename only
  //   removeFavouriteRoundType(id)         → unfavourite (204)
  listFavouriteRoundTypes: () => request("/api/favourite-round-types"),
  addFavouriteRoundType: (roundTypeId, name) =>
    request("/api/favourite-round-types", {
      method: "PUT",
      body: { round_type_id: roundTypeId, name },
    }),
  renameFavouriteRoundType: (roundTypeId, name) =>
    request(`/api/favourite-round-types/${roundTypeId}`, {
      method: "PATCH",
      body: { name },
    }),
  removeFavouriteRoundType: (roundTypeId) =>
    request(`/api/favourite-round-types/${roundTypeId}`, { method: "DELETE" }),

  submitFeedback: (payload) => request("/api/feedback", { method: "POST", body: payload }),
  getAnnouncements: () => request("/api/announcements"),
  recordPageview: (route) => request("/api/telemetry/pageview", { method: "POST", body: { route } }),
  exportData: () => fetch("/api/auth/export-data", { credentials: "include" }),
  deleteAccount: (confirm) =>
    request(`/api/auth/account?confirm=${encodeURIComponent(confirm)}`, { method: "DELETE" }),

  // ── Personal access tokens (the read-only data API at /api/export) ──
  // Cookie session only — a personal token can never mint or revoke another.
  // `createApiToken` is the ONE response that carries the plaintext; it is not
  // stored anywhere and no later read can return it.
  listApiTokens: () => request("/api/auth/tokens"),
  createApiToken: (payload) =>
    request("/api/auth/tokens", { method: "POST", body: payload }),
  revokeApiToken: (id) => request(`/api/auth/tokens/${id}`, { method: "DELETE" }),

  // ── RBTE (Read Between The Ends) — Between Ends mirror ──
  rbteStatus: () => request("/api/rbte/status"),
  rbteTournaments: (params = {}) => {
    const qs = new URLSearchParams();
    for (const k of ["state", "year", "month", "season", "q", "has_elims", "sort", "dir", "page", "per_page"]) {
      if (params[k] != null && params[k] !== "") qs.set(k, String(params[k]));
    }
    // `kind` repeats (kind=joad&kind=indoor): the backend ANDs them.
    for (const k of [].concat(params.kind ?? [])) if (k) qs.append("kind", String(k));
    const q = qs.toString();
    return request(`/api/rbte/tournaments${q ? `?${q}` : ""}`);
  },
  rbteTournament: (beId) => request(`/api/rbte/tournaments/${beId}`),
  // Recent averages for up to 10 categories (by name) of a big tournament
  // (detail `recent_averages_mode: "per_category"`); `{ rows: [{ row_key,
  // recent_average, stats_key }] }`. `signal` aborts on navigation.
  rbteCategoryRecentAverages: (beId, categories, { signal } = {}) => {
    const qs = new URLSearchParams();
    for (const c of categories) qs.append("category", String(c));
    return request(`/api/rbte/tournaments/${beId}/categories/recent-averages?${qs.toString()}`, { signal });
  },
  rbteLeaderboard: (params = {}) => {
    const qs = new URLSearchParams();
    for (const k of ["slice", "metric", "q", "archer", "sort", "dir", "season", "division", "format", "age_class",
                     "gender", "club_id", "state", "venue", "min_appearances", "page", "per_page"]) {
      if (params[k] != null && params[k] !== "") qs.set(k, String(params[k]));
    }
    return request(`/api/rbte/stats/leaderboard?${qs.toString()}`);
  },
  rbteEntity: (slice, key, params = {}) => {
    const qs = new URLSearchParams();
    for (const k of ["season", "division", "format"]) {
      const v = params[k];
      // `format` may be a list (a format family, #2528): repeated `format=`.
      if (Array.isArray(v)) v.filter((x) => x != null && x !== "").forEach((x) => qs.append(k, String(x)));
      else if (v != null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return request(`/api/rbte/stats/${slice}/${encodeURIComponent(key)}${q ? `?${q}` : ""}`);
  },
  rbteArcherSearch: (q) => request(`/api/rbte/search/archers?q=${encodeURIComponent(q)}`),
};

// ── M13-W2 anchor scaffold — each agent replaces ONLY its own placeholder line(s); leave the others as-is ──
// ── M13-W2 [#2319] Home tiles ──
// Every start-defaults row the active archer has, in one call (#2319) — Home's
// format tiles rank favourites + recently-used formats by `last_used_at`
// BEFORE capping the tile list at 6, so a per-tile `getFormatStartDefaults`
// fan-out would scale with the archer's library size. Same shape as that
// singular endpoint's rows; same no-cache reasoning (per-archer, mutated by
// every start). Assigned onto the already-declared `api` const (added AFTER
// its object literal closes) rather than inside it, so this anchor slot never
// collides with the other W2 agents' own additions to the same file.
api.listFormatStartDefaults = () =>
  request("/api/format-start-defaults", { cache: "no-cache" });
// ── M13-W2 [#2320] Format picker + Start sheet ──
// (#2320 2.2a Format picker: no new api helpers — reuses roundTypes(),
// recentRoundTypeIds(), listFavouriteRoundTypes()/addFavouriteRoundType()/
// removeFavouriteRoundType() above.)
// ── M13-W2 [#2321] Format editor ──
// (#2321: no new api helpers needed — createPersonalRoundType /
// updatePersonalRoundType / duplicateRoundType / archivePersonalRoundType /
// roundTypes / the favourite-round-types trio above already cover the
// Formats list + editor pages.)
// ── M13-W2 [#2322] Upload scorecard ──
// Archer-SCOPED reads for the Upload door's "whose card" switcher, which can
// point at a managed archer OTHER than the app's global active archer (a
// parent uploading a child's card). `getLoadouts()`/`getTags()` above both
// read the module-level X-Active-Archer header instead — swapping that
// globally for one page would leak into every other component reading the
// same header mid-navigation, so these pass an EXPLICIT per-call header
// override (shared/api-core.js's `request` merges it over the provider
// default) and never touch the shared reference cache, which is keyed
// without an archer dimension. `api` is a plain object (not frozen), so
// extending it post-declaration here is safe.
Object.assign(api, {
  getLoadoutsFor: (archerId) =>
    request("/api/equipment/loadouts", {
      headers: archerId != null ? { "X-Active-Archer": String(archerId) } : undefined,
    }),
  getTagsFor: (archerId) =>
    request("/api/tags", {
      headers: archerId != null ? { "X-Active-Archer": String(archerId) } : undefined,
    }),
});
// ── M13-W2 [#2323] Save-format nudge ──
// (#2323: no new api helpers needed — createPersonalRoundType (above) already
// covers the unmatched-shape "Save this as a format?" path, auto-favouriting
// the new personal Format server-side; the matched-shape "use it next time?"
// path favourites the EXISTING catalog row via addFavouriteRoundType (above)
// so it starts showing on Home. Writing per-archer start-defaults for that
// row (#2317) happens the same way it already does for every Format — by
// actually starting it — so there is nothing new to write here without the
// archer starting the round.)
// ── M13-W2 end ──
